import { apiClient } from "./client";

/**
 * Create a payment order via backend (Cashfree Payments).
 */
export async function createPaymentOrder(type, extras = {}) {
  const res = await apiClient.post("/payments/create-order", {
    type,
    ...extras,
  });
  return res.data;
}

/**
 * Ask backend to verify and capture the payment after checkout widget completion.
 */
// The fields POST /payments/verify accepts (VerifyPaymentDto). The server
// refuses any other, and the request note is saved with the order: sending
// it here turned a successful payment into "Transaction Failed".
const VERIFY_FIELDS = [
  "provider",
  "orderId",
  "paymentId",
  "signature",
  "razorpayOrderId",
  "razorpayPaymentId",
  "razorpaySignature",
];

export async function verifyPayment(payload) {
  // Supports the payload shape { provider, orderId, paymentId, signature }
  // or legacy positional parameters
  const given =
    typeof payload === "string"
      ? {
          orderId: arguments[0],
          paymentId: arguments[1],
          signature: arguments[2],
        }
      : payload;
  const body = Object.fromEntries(
    VERIFY_FIELDS.filter((key) => given[key] !== undefined).map((key) => [
      key,
      given[key],
    ]),
  );

  const res = await apiClient.post("/payments/verify", body);
  return res.data;
}

/** Dynamic script loader for Cashfree */
let cashfreeLoadPromise = null;
export function loadCashfreeScript() {
  if (typeof window === "undefined")
    return Promise.reject(new Error("Not in browser"));
  if (window.Cashfree) return Promise.resolve();
  if (cashfreeLoadPromise) return cashfreeLoadPromise;

  cashfreeLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Cashfree SDK script"));
    document.body.appendChild(script);
  });

  return cashfreeLoadPromise;
}

/**
 * Universal pay() helper that uses Cashfree Payments.
 */
export async function pay({
  type,
  provider = "CASHFREE",
  credentialId,
  verifierType,
  verifierId,
  requestNote,
  idempotencyKey,
}) {
  // Step 1: backend creates the Cashfree order
  const order = await createPaymentOrder(type, {
    provider: "CASHFREE",
    credentialId,
    verifierType,
    verifierId,
    requestNote,
    idempotencyKey,
  });

  // Bypass path (development/staging without active gateway keys)
  if (order?.bypass) {
    const verified = await verifyPayment({
      provider: "CASHFREE",
      orderId: order.orderId,
      paymentId: "bypass-no-payment",
      signature: "bypass-no-signature",
      requestNote,
    });
    return verified;
  }

  // Step 2: Launch Cashfree checkout
  return handleCashfreeCheckout(order, requestNote);
}

export async function handleCashfreeCheckout(order, requestNote) {
  await loadCashfreeScript();

  // Must match the backend gateway's CASHFREE_ENVIRONMENT, otherwise Cashfree
  // rejects the session id ("payment_session_id_invalid"). The Docker build
  // derives this from the same .env value the backend uses.
  const isProduction =
    (
      import.meta.env.VITE_CASHFREE_ENVIRONMENT ||
      import.meta.env.VITE_CASHFREE_MODE ||
      ""
    ).toLowerCase() === "production" ||
    (typeof window !== "undefined" &&
      (window.location.hostname === "tickandpass.com" ||
        window.location.hostname === "www.tickandpass.com"));

  const cashfree = window.Cashfree({
    mode: isProduction ? "production" : "sandbox",
  });

  const checkoutOptions = {
    paymentSessionId: order.paymentSessionId,
    redirectTarget: "_modal",
  };

  return new Promise((resolve, reject) => {
    cashfree
      .checkout(checkoutOptions)
      .then(async (result) => {
        if (result.error) {
          if (
            result.error.code === "MODAL_CLOSED" ||
            result.error.message?.includes("closed")
          ) {
            return reject(
              Object.assign(new Error("Payment cancelled"), {
                code: "ERR_CANCELLED",
              }),
            );
          }
          return reject(
            Object.assign(
              new Error(result.error.message || "Cashfree payment failed"),
              { code: "ERR_PAYMENT_FAILED" },
            ),
          );
        }

        if (result.redirect) {
          // Redirection mode completed
          return resolve({ redirected: true });
        }

        if (result.paymentDetails) {
          try {
            const verified = await verifyPayment({
              provider: "CASHFREE",
              orderId: order.orderId,
              paymentId: result.paymentDetails.paymentId || order.orderId,
              requestNote,
            });
            resolve(verified);
          } catch (err) {
            reject(err);
          }
        } else {
          // Modal completed / closed, perform verification check with backend
          try {
            const verified = await verifyPayment({
              provider: "CASHFREE",
              orderId: order.orderId,
              paymentId: order.orderId,
              requestNote,
            });
            resolve(verified);
          } catch (err) {
            reject(err);
          }
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
}
